import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../components/layout/Layout";
import "./screen.css";
import DesktopMacIcon from "@mui/icons-material/DesktopMac";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import { deleteScreen, addScreen, getScreens, screenFlagOff, addPlaylistToScreen, assignDevice } from "../../Redux/Actions/screenAction";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as AddScreenIcon } from "../../Assets/images/addScreenIcon.svg";
import PrimarySearchBar from "../../components/searchBar/searchbar";
import DeleteModel from "../../components/deletemodel/DeleteModel";
import { getPlaylist } from "../../Redux/Actions/playlistAction";
import { getContent } from "../../Redux/Actions/contentAction";
import toast from "react-hot-toast";
import { api } from "../../api/api";

const CustomTableCell = styled(TableCell)(() => ({
  color: '#15192C',
  fontSize: '14px',
  fontWeight: '700',
}));

function Screen({ socketRef, user }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { screens, isAdded, isScreenDeleted, mac, screenLoading, error } = useSelector(
    (state) => state.Screen
  );
  const { playlist } = useSelector((state) => state.Playlist);
  const [openModel, setOpenModel] = useState(false);
  const [screenId, setScreenId] = useState({ id: '', mac: '' });
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const macRef = useRef();
  const [textVal, setTextVal] = useState(() => sessionStorage.getItem('screenFilter') || '');
  const [filteredScreen, setFilteredScreen] = useState([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [openBulkDialog, setOpenBulkDialog] = useState(false);
  const [bulkPlaylistId, setBulkPlaylistId] = useState("");
  const [playlistSearch, setPlaylistSearch] = useState("");
  const [playlistSort, setPlaylistSort] = useState("name-asc");
  const [openAssignDevice, setOpenAssignDevice] = useState(false);
  const [assignTargetScreen, setAssignTargetScreen] = useState(null);
  const [deviceCode, setDeviceCode] = useState("");
  const [multiSelectMode, setMultiSelectMode] = useState(false);

  const onMultiSelectModeChange = useCallback((e) => {
    const on = e.target.checked;
    setMultiSelectMode(on);
    if (!on) setCheckedIds(new Set());
  }, []);

  useEffect(() => {
    dispatch(getPlaylist(user?._id));
    dispatch(getScreens(user?._id));
    dispatch(getContent(user?._id));
  }, []);

  useEffect(() => {
    if (screens.length === 0) return;
    const filterParam = new URLSearchParams(location.search).get('filter');
    if (filterParam === 'offline') {
      setFilteredScreen(screens.filter(s => s.status === false));
      return;
    }
    const saved = sessionStorage.getItem('screenFilter');
    if (saved && saved !== '') {
      setFilteredScreen(screens.filter(s =>
        Object.values(s).join('').toLocaleLowerCase().includes(saved.toLocaleLowerCase())
      ));
    } else {
      setFilteredScreen([...screens]);
    }
  }, [screens, location.search]);

  useEffect(() => {
    if (isAdded) {
      const saved = sessionStorage.getItem('screenFilter') || '';
      setFilteredScreen(saved
        ? screens.filter(s => Object.values(s).join('').toLocaleLowerCase().includes(saved.toLocaleLowerCase()))
        : [...screens]
      );
      dispatch(screenFlagOff());
    }
    if (isScreenDeleted) {
      setFilteredScreen([...screens]);
      dispatch(screenFlagOff());
    }
  }, [isAdded, isScreenDeleted]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => { setRowsPerPage(+e.target.value); setPage(0); };

  const emitOnDeleteScreen = (mac) => {
    const id = setTimeout(() => { socketRef.current?.emit("screen_deleted", { detail: { mac } }); clearTimeout(id); }, 2000);
  };
  const deleteScreenHandler = (id, mac) => { setScreenId({ id, mac }); setOpenDeleteDialog(true); };
  const confirmDeleteScreen = () => { dispatch(deleteScreen(screenId.id)); emitOnDeleteScreen(screenId.mac); setOpenDeleteDialog(false); };

  const emitOnAddedScreen = () => {
    const id = setTimeout(() => { socketRef.current?.emit("screen_added", { detail: { mac: macRef.current.value, code } }); clearTimeout(id); }, 2000);
  };
  const submit = () => {
    dispatch(addScreen({ name, user: user?._id, subuser: user?._id, role: user?.role }, code));
    setOpenModel(false);
    emitOnAddedScreen();
    setCode(""); setName("");
  };

  const submitAssignDevice = () => {
    if (!deviceCode.trim() || !assignTargetScreen) return;
    dispatch(assignDevice(assignTargetScreen._id, deviceCode.trim()));
    setOpenAssignDevice(false);
    setDeviceCode("");
    setAssignTargetScreen(null);
  };

  const searchHandler = (e) => {
    setTextVal(e);
    sessionStorage.setItem('screenFilter', e);
    setFilteredScreen(e !== ''
      ? screens.filter(s => Object.values(s).join('').toLocaleLowerCase().includes(e.toLocaleLowerCase()))
      : [...screens]
    );
  };
  const resetFilter = () => { setTextVal(''); sessionStorage.removeItem('screenFilter'); setFilteredScreen([...screens]); };

  const toggleCheck = (id) => {
    setCheckedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleCheckAll = () => {
    const pageIds = filteredScreen.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(s => s._id);
    const allChecked = pageIds.every(id => checkedIds.has(id));
    setCheckedIds(prev => { const n = new Set(prev); pageIds.forEach(id => allChecked ? n.delete(id) : n.add(id)); return n; });
  };
  const applyBulkPlaylist = () => {
    if (!bulkPlaylistId) return;
    checkedIds.forEach(id => {
      dispatch(addPlaylistToScreen({ id, value: bulkPlaylistId }));
      const screen = screens.find(s => s._id === id);
      if (screen) { const t = setTimeout(() => { socketRef.current?.emit("playlist_added", { detail: { mac: screen.mac, code: id } }); clearTimeout(t); }, 2000); }
    });
    setCheckedIds(new Set()); setBulkPlaylistId(""); setOpenBulkDialog(false);
  };
  const navigateToSetting = (id) => {
    sessionStorage.setItem('screenSettingCarouselIds', JSON.stringify(filteredScreen.map((s) => s._id)));
    sessionStorage.setItem('screenFilteredIds', JSON.stringify([id]));
    navigate(`/setting/${id}`);
  };

  const pagedScreens = filteredScreen.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getFilteredPlaylists = () => {
    const all = (playlist || []);
    const searched = all.filter(p => p.name?.toLowerCase().includes(playlistSearch.toLowerCase()));
    return [...searched].sort((a, b) => {
      if (playlistSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
      if (playlistSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
      if (playlistSort === 'date-desc') return new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0);
      return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0);
    });
  };

  return (
    <>
      <Layout title={"Screen"} user={user}>
        <input ref={macRef} hidden value={mac} />

        {/* 검색바 + 버튼 */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 160, background: 'white', borderRadius: '12px', px: 1 }}>
            <PrimarySearchBar value={textVal} onChange={searchHandler} />
          </Box>
          {textVal && (
            <button onClick={resetFilter} style={{ height: 40, background: 'none', border: '1px solid #ccc', borderRadius: '8px', padding: '0 14px', cursor: 'pointer', color: '#888', fontSize: '13px' }}>
              초기화
            </button>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, height: 40, padding: '0 12px', background: multiSelectMode ? 'rgba(85,31,255,0.08)' : 'white', border: multiSelectMode ? '1px solid #551FFF' : '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: multiSelectMode ? '#551FFF' : '#555', userSelect: 'none', flexShrink: 0 }}>
            <input type="checkbox" checked={multiSelectMode} onChange={onMultiSelectModeChange} style={{ cursor: 'pointer', width: 16, height: 16 }} />
            일괄 플레이리스트 변경
          </label>
          <button className="add-screen-btn" style={{ height: 40, width: 'auto', padding: '0 20px', borderRadius: '8px', flexShrink: 0 }} onClick={() => setOpenModel(true)}>
            + Add Screen
          </button>
        </Box>

        {/* 일괄 선택 툴바 */}
        {multiSelectMode && checkedIds.size > 0 && (
          <Box sx={{ mx: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, background: 'rgba(85,31,255,0.05)', borderRadius: '8px' }}>
            <span style={{ fontSize: '13px', color: '#551FFF' }}>{checkedIds.size}개 선택됨</span>
            <button onClick={() => setOpenBulkDialog(true)} style={{ background: '#551FFF', color: 'white', border: 'none', borderRadius: '6px', padding: '5px 14px', fontSize: '12px', cursor: 'pointer' }}>
              플레이리스트 변경
            </button>
            <button onClick={() => setCheckedIds(new Set())} style={{ background: 'none', color: '#888', border: '1px solid #ccc', borderRadius: '6px', padding: '5px 10px', fontSize: '12px', cursor: 'pointer' }}>
              취소
            </button>
          </Box>
        )}

        {/* 데스크탑: 테이블 */}
        <Box className="screen-desktop-view" sx={{ mx: 2, background: 'white', borderRadius: '16px', overflow: 'hidden' }}>
          <div className="screen-table">
            <Table>
              <TableHead>
                <TableRow style={{ borderBottom: "2px solid #551fff" }}>
                  {multiSelectMode && (
                    <CustomTableCell align="center" padding="checkbox">
                      <input type="checkbox" style={{ cursor: 'pointer' }}
                        onChange={toggleCheckAll}
                        checked={pagedScreens.length > 0 && pagedScreens.every(s => checkedIds.has(s._id))}
                      />
                    </CustomTableCell>
                  )}
                  <CustomTableCell align="center">No.</CustomTableCell>
                  <CustomTableCell align="center">Code</CustomTableCell>
                  <CustomTableCell align="center">Name</CustomTableCell>
                  <CustomTableCell align="center">MAC</CustomTableCell>
                  <CustomTableCell align="center">Playlist</CustomTableCell>
                  <CustomTableCell align="center">Status</CustomTableCell>
                  <CustomTableCell align="center">Actions</CustomTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {screenLoading ? (
                  <TableRow><TableCell colSpan={multiSelectMode ? 8 : 7} align="center">
                    <img src="/images/spiner.gif" style={{ height: 100, width: 100 }} alt="loading" />
                  </TableCell></TableRow>
                ) : pagedScreens.map((screen, index) => (
                  <TableRow key={screen._id} selected={multiSelectMode && checkedIds.has(screen._id)} hover>
                    {multiSelectMode && (
                      <TableCell align="center" padding="checkbox">
                        <input type="checkbox" style={{ cursor: 'pointer' }} checked={checkedIds.has(screen._id)} onChange={() => toggleCheck(screen._id)} />
                      </TableCell>
                    )}
                    <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell align="center">{screen.code}</TableCell>
                    <TableCell align="center">{screen.name}</TableCell>
                    <TableCell align="center" sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{screen.mac}</TableCell>
                    <TableCell align="center">
                      <span style={{ fontSize: '12px', color: screen.playlist?.name ? '#551FFF' : '#aaa' }}>
                        {screen.playlist?.name || '-'}
                      </span>
                    </TableCell>
                    <TableCell align="center">
                      <div className={`status-badge ${screen.status ? 'success' : 'danger'}`} />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                        {screen.mac?.startsWith('PLH_') && (
                          <button onClick={() => { setAssignTargetScreen(screen); setOpenAssignDevice(true); }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#F7A800', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(247,168,0,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                            📱 기기 연결
                          </button>
                        )}
                        <button onClick={() => navigateToSetting(screen._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#551FFF', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(85,31,255,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <SettingsIcon style={{ fontSize: 16 }} /> 설정
                        </button>
                        <button onClick={() => deleteScreenHandler(screen._id, screen.mac)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#FF3B3B', display: 'flex', alignItems: 'center', gap: 2, fontSize: '12px', padding: '4px 8px', borderRadius: '6px' }}
                          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,59,59,0.08)'}
                          onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                          <DeleteOutlineIcon style={{ fontSize: 16 }} /> 삭제
                        </button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 20, { label: `전체 (${filteredScreen.length})`, value: filteredScreen.length }]}
              component="div"
              count={filteredScreen.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </Box>

        {/* 모바일: 카드 */}
        <Box className="screen-mobile-view" sx={{ px: 2, pb: 2 }}>
          {screenLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <img src="/images/spiner.gif" style={{ height: 80, width: 80 }} alt="loading" />
            </Box>
          ) : filteredScreen.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, color: '#aaa', fontSize: '14px' }}>스크린이 없습니다.</Box>
          ) : (
            <>
              {multiSelectMode && (
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <label style={{ fontSize: '12px', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="checkbox"
                      onChange={() => {
                        const allChecked = filteredScreen.every(s => checkedIds.has(s._id));
                        setCheckedIds(prev => { const n = new Set(prev); filteredScreen.forEach(s => allChecked ? n.delete(s._id) : n.add(s._id)); return n; });
                      }}
                      checked={filteredScreen.length > 0 && filteredScreen.every(s => checkedIds.has(s._id))}
                      style={{ cursor: 'pointer' }}
                    />
                    전체 선택
                  </label>
                  <span style={{ fontSize: '12px', color: '#aaa' }}>총 {filteredScreen.length}개</span>
                </Box>
              )}
              <div className="screen-cards-grid">
                {filteredScreen.map((screen) => (
                  <div key={screen._id} className={`screen-card ${multiSelectMode && checkedIds.has(screen._id) ? 'screen-card--checked' : ''}`}>
                    <div className="screen-card-header">
                      {multiSelectMode && (
                        <input type="checkbox" checked={checkedIds.has(screen._id)} onChange={() => toggleCheck(screen._id)}
                          style={{ cursor: 'pointer', flexShrink: 0 }} onClick={e => e.stopPropagation()} />
                      )}
                      <DesktopMacIcon style={{ color: '#551FFF', fontSize: 20 }} />
                      <span className="screen-card-name">{screen.name || '-'}</span>
                      <div className={`status-badge ${screen.status ? 'success' : 'danger'}`} style={{ marginLeft: 'auto', flexShrink: 0 }} />
                    </div>
                    <div className="screen-card-body" onClick={() => navigateToSetting(screen._id)}>
                      <div className="screen-card-row">
                        <span className="screen-card-label">Code</span>
                        <span className="screen-card-value">{screen.code || '-'}</span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">MAC</span>
                        <span className="screen-card-value screen-card-mono">{screen.mac || '-'}</span>
                      </div>
                      <div className="screen-card-row">
                        <span className="screen-card-label">Playlist</span>
                        <span className="screen-card-value" style={{ color: screen.playlist?.name ? '#551FFF' : '#ccc' }}>
                          {screen.playlist?.name || '미지정'}
                        </span>
                      </div>
                    </div>
                    <div className="screen-card-actions">
                      {screen.mac?.startsWith('PLH_') && (
                        <button className="screen-card-btn" onClick={() => { setAssignTargetScreen(screen); setOpenAssignDevice(true); }}
                          style={{ color: '#F7A800', border: '1px solid rgba(247,168,0,0.4)', background: 'none', borderRadius: '6px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          📱 기기 연결
                        </button>
                      )}
                      <button className="screen-card-btn screen-card-btn--settings" onClick={() => navigateToSetting(screen._id)}>
                        <SettingsIcon style={{ fontSize: 16 }} /> 설정
                      </button>
                      <button className="screen-card-btn screen-card-btn--delete" onClick={() => deleteScreenHandler(screen._id, screen.mac)}>
                        <DeleteOutlineIcon style={{ fontSize: 16 }} /> 삭제
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Box>
      </Layout>

      <DeleteModel open={openDeleteDialog} onClose={(e) => setOpenDeleteDialog(e)} onConfirm={confirmDeleteScreen} />

      {/* Add Screen Dialog */}
      <Dialog fullWidth open={openModel} onClose={() => setOpenModel(false)}>
        <DialogTitle style={{ textAlign: "center", borderBottom: "2px solid #551fff", fontSize: '18px', fontWeight: 700 }}>Add Screen</DialogTitle>
        <DialogContent>
          <div className="screen-form">
            <div className="icon-screen"><AddScreenIcon /></div>
            <div className="form-fields">
              <TextField value={code} onChange={(e) => setCode(e.target.value)} type="text" variant="standard" label="Type Screen Code" fullWidth sx={{ label: { color: '#92959E', fontSize: '14px' } }} />
            </div>
            <div className="form-fields">
              <TextField value={name} onChange={(e) => setName(e.target.value)} type="text" variant="standard" label="Type Screen Name" fullWidth sx={{ label: { color: '#92959E', fontSize: '14px' } }} />
            </div>
            <Box sx={{ display: 'flex', mt: 6, mb: 4, justifyContent: 'center', gap: 2 }}>
              <button style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px' }} onClick={() => setOpenModel(false)}>Cancel</button>
              <button style={{ background: '#551FFF', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px' }} onClick={submit}>Save</button>
            </Box>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Device Dialog */}
      <Dialog fullWidth maxWidth="xs" open={openAssignDevice} onClose={() => { setOpenAssignDevice(false); setDeviceCode(""); setAssignTargetScreen(null); }}>
        <DialogTitle style={{ textAlign: 'center', borderBottom: '2px solid #551fff', fontSize: '18px', fontWeight: 700 }}>기기 연결</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {assignTargetScreen && (
              <p style={{ fontSize: '13px', color: '#92959E', textAlign: 'center', margin: 0 }}>
                <strong style={{ color: '#15192C' }}>{assignTargetScreen.name}</strong> 스크린에 연결할 기기 코드를 입력하세요.
              </p>
            )}
            <TextField
              value={deviceCode}
              onChange={e => setDeviceCode(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitAssignDevice(); }}
              type="text"
              variant="standard"
              label="기기 코드 (TV 앱에서 제공)"
              fullWidth
              autoFocus
              sx={{ label: { color: '#92959E', fontSize: '14px' } }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
              <button onClick={() => { setOpenAssignDevice(false); setDeviceCode(""); setAssignTargetScreen(null); }}
                style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px', cursor: 'pointer' }}>
                취소
              </button>
              <button onClick={submitAssignDevice} disabled={!deviceCode.trim()}
                style={{ background: deviceCode.trim() ? '#551FFF' : '#ccc', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px', cursor: deviceCode.trim() ? 'pointer' : 'default' }}>
                연결
              </button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 일괄 플레이리스트 변경 Dialog */}
      <Dialog fullWidth open={openBulkDialog} onClose={() => { setOpenBulkDialog(false); setPlaylistSearch(""); }}>
        <DialogTitle style={{ textAlign: 'center', borderBottom: '2px solid #551fff', fontSize: '18px', fontWeight: 700 }}>플레이리스트 일괄 변경</DialogTitle>
        <DialogContent>
          <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <p style={{ fontSize: '13px', color: '#92959E', textAlign: 'center', margin: 0 }}>선택된 {checkedIds.size}개 스크린에 적용할 플레이리스트를 선택하세요.</p>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input value={playlistSearch} onChange={e => setPlaylistSearch(e.target.value)} placeholder="이름 검색..."
                style={{ flex: 1, height: '36px', border: '1px solid rgba(85,31,255,0.3)', borderRadius: '8px', padding: '0 10px', fontSize: '13px', outline: 'none' }} />
              <select value={playlistSort} onChange={e => setPlaylistSort(e.target.value)}
                style={{ height: '36px', border: '1px solid rgba(85,31,255,0.3)', borderRadius: '8px', padding: '0 8px', fontSize: '12px', color: '#555' }}>
                <option value="name-asc">이름 A→Z</option>
                <option value="name-desc">이름 Z→A</option>
                <option value="date-desc">최신순</option>
                <option value="date-asc">오래된순</option>
              </select>
            </Box>
            <Box sx={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid rgba(85,31,255,0.15)', borderRadius: '8px' }}>
              {getFilteredPlaylists().length === 0
                ? <p style={{ textAlign: 'center', color: '#aaa', fontSize: '13px', padding: '20px' }}>결과 없음</p>
                : getFilteredPlaylists().map(p => (
                  <Box key={p._id} onClick={() => setBulkPlaylistId(p._id)}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, cursor: 'pointer', borderBottom: '1px solid rgba(0,0,0,0.05)',
                      background: bulkPlaylistId === p._id ? 'rgba(85,31,255,0.08)' : 'white', '&:hover': { background: 'rgba(85,31,255,0.05)' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', border: '2px solid #551FFF', background: bulkPlaylistId === p._id ? '#551FFF' : 'white', flexShrink: 0 }} />
                      <span style={{ fontSize: '14px', color: '#15192C' }}>{p.name}</span>
                    </Box>
                    <span style={{ fontSize: '11px', color: '#aaa' }}>{p.content?.length || 0}개 항목</span>
                  </Box>
                ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <button onClick={() => { setOpenBulkDialog(false); setPlaylistSearch(""); }} style={{ background: 'white', padding: '8px', width: '130px', color: '#551FFF', border: '1px solid #551FFF', borderRadius: '5px', cursor: 'pointer' }}>취소</button>
              <button onClick={applyBulkPlaylist} disabled={!bulkPlaylistId} style={{ background: bulkPlaylistId ? '#551FFF' : '#ccc', padding: '8px', width: '130px', color: 'white', border: 'none', borderRadius: '5px', cursor: bulkPlaylistId ? 'pointer' : 'default' }}>적용</button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default Screen;
